<script setup>
import { cloneDeep, isEmpty, isNull } from 'lodash'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SlickItem, SlickList, HandleDirective as vHandle } from 'vue-slicksort'

import HomepageSidebarSegmentTitle from '@/components/home-page/HomepageSidebarSegmentTitle'
import {
  SIDEBAR_PINNED_ITEM_DRAG_HANDLE_TEST_ID,
  SIDEBAR_PINNED_LIST_TEST_ID
} from '@/components/home-page/jest-helpers'
import AppIcon from '@/components/ui/AppIcon/AppIcon'

defineOptions({
  name: 'HomepageSidebarPinnedList',
  inheritAttrs: false
})

const props = defineProps({
  items: {
    type: Array,
    required: true
  },

  itemKey: {
    type: String,
    default: 'id'
  },

  disabled: {
    type: Boolean
  }
})

const emit = defineEmits({
  'update:items': items => Array.isArray(items)
})

const { t } = useI18n()

const isPinnedItemsExist = computed(() => !isEmpty(props.items))

const onSortEnd = ({ oldIndex, newIndex }) => {
  if (isNull(newIndex) || oldIndex === newIndex) {
    return
  }

  const clonedList = cloneDeep(props.items)

  clonedList.splice(newIndex, 0, clonedList.splice(oldIndex, 1)[0])

  emit('update:items', clonedList)
}
</script>

<template>
  <template v-if="isPinnedItemsExist">
    <HomepageSidebarSegmentTitle>{{ t('common.pinned') }}</HomepageSidebarSegmentTitle>

    <SlickList
      :data-auto-testid="SIDEBAR_PINNED_LIST_TEST_ID"
      :data-testid="SIDEBAR_PINNED_LIST_TEST_ID"
      :distance="10"
      :dragged-settling-duration="1"
      :list="items"
      :lock-offset="['5%', '5%']"
      :transition-duration="200"
      class="hspl-Wrapper"
      helper-class="hspl-Item-grabbing"
      lock-axis="y"
      lock-to-container-edges
      use-drag-handle
      @sort-end="onSortEnd({ ...$event })"
    >
      <div class="hspl-List">
        <SlickItem
          v-for="(item, index) in items"
          :key="item[itemKey]"
          :disabled="disabled"
          :index="index"
          class="hspl-Item"
        >
          <div
            v-handle
            :class="{ 'hspl-Drag-disabled': disabled }"
            :data-auto-testid="SIDEBAR_PINNED_ITEM_DRAG_HANDLE_TEST_ID"
            :data-testid="SIDEBAR_PINNED_ITEM_DRAG_HANDLE_TEST_ID"
            class="hspl-Drag"
          >
            <AppIcon height="40" icon-name="homepage-pins-drag" width="16" />
          </div>

          <div class="hspl-Item_Content">
            <slot :item="item" name="item" />
          </div>
        </SlickItem>
      </div>
    </SlickList>
  </template>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/dnd';
@import '~@/assets/styles/homepage-mixins';

.hspl-Wrapper {
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: min(40%, 320px);
  padding: 0 var(--ob-hps-mx) 16px 0;
}

.hspl-List {
  @include homepage-sidebar-list;
  min-width: calc($homepage-sidebar-list-min-width + var(--ob-hps-mx));
  user-select: none;
}

.hspl-Item {
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: var(--ob-hpgf-item-gap);
  }
}

.hspl-Item_Content {
  flex: 1;
  // a flex item cannot shrink below its content width by default,
  // so without this a long name would overflow the list instead of being truncated
  min-width: 0;
}

.hspl-Drag {
  cursor: grab;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: var(--ob-hps-mx);
  opacity: 0;

  &-disabled {
    pointer-events: none;
  }

  .hspl-Item-grabbing & {
    cursor: grabbing;
  }

  &:not(&-disabled) {
    .hspl-Item:hover & {
      opacity: 1;
    }
  }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector
.hspl-Item-grabbing {
  @extend %grabbing-item-common-styles;

  > * {
    pointer-events: none;
  }
}
</style>
